import type { ApiErrorDetail, ParsedError } from '@/api/types'
import { translate } from '@/i18n'

/**
 * Backend error classes (backend/src/errors.ts) → localized message keys.
 * The API contract is English-only (spec 010, decision 5): the frontend maps
 * the stable `error` class field to a localized template and preserves the
 * raw API message as a detail line.
 */
const ERROR_CLASS_TO_KEY: Record<string, string> = {
  ValidationError: 'errors.validation',
  UnauthorizedError: 'errors.unauthorized',
  ForbiddenError: 'errors.forbidden',
  NotFoundError: 'errors.notFound',
  ConflictError: 'errors.conflict',
  OptimisticLockError: 'errors.optimisticLock',
  InvalidOperationError: 'errors.invalidOperation',
  TooManyRequestsError: 'errors.tooManyRequests',
  ServiceUnavailableError: 'errors.serviceUnavailable',
  StorageObjectNotFoundError: 'errors.storageObjectNotFound',
}

/**
 * Normalizes backend error details into the ApiErrorDetail[] shape used by
 * FormField. The backend sends two shapes:
 * - array: [{ field, path: string[], message }] (custom ValidationErrors)
 * - object: { "config.smtp.auth.pass": ["message", ...] } (Zod validation)
 */
function normalizeDetails(details: unknown): ApiErrorDetail[] | undefined {
  if (Array.isArray(details)) return details as ApiErrorDetail[]
  if (details && typeof details === 'object') {
    return Object.entries(details as Record<string, unknown>).map(([path, messages]) => ({
      field: path,
      path: path.split('.'),
      message: Array.isArray(messages) ? messages.join('; ') : String(messages),
    }))
  }
  return undefined
}

export function parseApiError(err: unknown): ParsedError {
  if (err && typeof err === 'object' && 'response' in err) {
    const axiosErr = err as { response?: { status?: number; data?: { error?: string; message?: string; details?: unknown } } }
    const data = axiosErr.response?.data
    const status = axiosErr.response?.status

    if (data?.error) {
      const errorKey = ERROR_CLASS_TO_KEY[data.error]
      if (errorKey) {
        const message = translate(errorKey)
        const rawMessage = data.message && data.message !== message ? data.message : undefined
        return { message, rawMessage, details: normalizeDetails(data.details) }
      }
    }

    if (status) {
      const rawMessage = data?.message
      return { message: translate('errors.requestFailed', { status }), rawMessage }
    }

    return { message: data?.message || translate('errors.unexpected') }
  }

  // Axios network failures (no response at all)
  if (err instanceof Error && err.message === 'Network Error') {
    return { message: translate('errors.network') }
  }

  if (err instanceof Error) {
    return { message: err.message || translate('errors.unexpected') }
  }

  return { message: translate('errors.unexpected') }
}

/**
 * Like `parseApiError`, but for requests that asked for a binary body
 * (`responseType: 'blob'`): on a non-2xx the JSON error arrives as a Blob,
 * so this parses the JSON out of it first. Non-blob errors fall through to
 * `parseApiError` unchanged.
 */
export async function parseBlobApiError(err: unknown): Promise<ParsedError> {
  const axiosErr = err as { response?: { status?: number; data?: unknown } } | undefined
  const data = axiosErr?.response?.data
  if (data instanceof Blob) {
    const status = axiosErr?.response?.status
    try {
      const parsed: unknown = JSON.parse(await data.text())
      if (parsed && typeof parsed === 'object') {
        return parseApiError({ response: { status, data: parsed } })
      }
    } catch {
      // Not JSON (e.g. a partial/aborted body) — fall through to the generic handling
    }
    return { message: translate('errors.requestFailed', { status: status ?? 0 }) }
  }
  return parseApiError(err)
}

export function getFieldError(
  error: ParsedError | null,
  path: string | number | (string | number)[]
): ApiErrorDetail | null {
  if (!error?.details?.length) return null
  const normalized: (string | number)[] = Array.isArray(path) ? path : [path]
  for (const d of error.details) {
    if (d.path && d.path.length === normalized.length && d.path.every((seg, i) => String(seg) === String(normalized[i]))) {
      return d
    }
  }
  return null
}
